import { Component, ElementRef, inject, input, ViewChildren, QueryList } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { AuthUser } from '../../../core/models/auth/auth.interface';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { BaseComponent } from '../../../core/shared/base.component';
import { AuthService } from '../../../core/services/auth.service';
import Swal from 'sweetalert2';
import { LoaderComponent } from "../../../core/shared/spinner.component";
import { LoaderService } from '../../../core/shared/services/loader.service';
import { ERROR_MESSAGES } from '../../../core/shared/constants';

@Component({
  selector: 'app-verification',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, LoaderComponent],
  templateUrl: './verification.component.html',
  styleUrl: './verification.component.scss'
})
export class VerificationComponent extends BaseComponent {
  activatedRoute = inject(ActivatedRoute);
  loader = inject(LoaderService);
  
  @ViewChildren('codeInput') codeInputs!: QueryList<ElementRef>;

  authUser?: AuthUser;
  timer: string = '2:00';
  countdown: any;

  activeTimer = false;
  showSendVerification = false;
  validationOk = false;

  model: any = {
    validate: () => {
      for (let index = 1; index <= 5; index++) {
        if (!(this.model[`code${index}`] != null) || this.model[`code${index}`].toString().length == 0) {
          return false
        }
      }
      return true;
    }
  };
  validationError: string = '';

  constructor(private router: Router, private authService: AuthService) {
    super()

  }

  ngOnInit(): void {

    this.loader.show();
    this.activatedRoute.queryParams.subscribe(params => {
      const jwtUser = params['jwtUser'] || this.authService.getToken();
      if (!jwtUser) {
        this.errorDefault();
        this.authService.signOutApply();
        this.router.navigate(['/']);
      }

      this.authUser = jwtDecode(jwtUser);
      this.authService.signInApply(jwtUser);

      this.loader.hide();

    });

    this.startTimer();
  }

  startTimer() {
    this.activeTimer = true;
    let totalSeconds = 20; //TODO: cambiar a 120
    this.countdown = setInterval(() => {
      if (totalSeconds <= 0) {
        clearInterval(this.countdown);
        this.activeTimer = false;
        this.showSendVerification = true;
      } else {
        totalSeconds--;
        this.timer = this.formatTime(totalSeconds);
      }
    }, 1000);
  }

  formatTime(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  verifyCode() {
    const code = `${this.model.code1}${this.model.code2}${this.model.code3}${this.model.code4}${this.model.code5}`;
    this.loader.show();
    this.authService.verifyCode(code, this.authUser!!.id)
      .subscribe({
        next: (res: any) => {
          console.log('verifyCode-res', res);
          if (res.data) {
            this.showSuccessMessage("Cuenta validada exitosamente");
            this.validationOk = true;
            this.validationError = '';
          } else {
            this.validationError = ERROR_MESSAGES.DefaultInvalidOTPCode.message;
          }
          this.loader.hide();
          
        },
        error: err => {
          if (err.status == 404)
            this.validationError = err.error.message;
          else
            this.errorDefault();
          this.loader.hide();
        },
        complete: () => {
          this.loader.hide();
        }
      });

  }

  showSuccessMessage(message: string): void {
    Swal.fire({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 5000, // 5 segundos
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.onmouseenter = Swal.stopTimer;
        toast.onmouseleave = Swal.resumeTimer;
      },
      html: message,
      icon: 'success',
    });
  }

  moveToNext(event: any, currentIndex: number): void {
    this.validationError = '';
    const input = event.target as HTMLInputElement;

    if (input.value && currentIndex < 5) {
      const nextInput = (input.parentElement!.children[currentIndex] as HTMLInputElement);
      nextInput.focus();
    } else {
      if (event.inputType != 'deleteContentBackward')
        input.blur();
    }
    if (this.model.validate()) {
      this.verifyCode();
    }
  }

  validateKey(event: any) {
    console.log('validateKey');
    if (event.key === 'Delete' || event.key === 'Backspace' || event.key === 'Tab') {
      return true;
    }
    if (event.target.value.length > 0) {
      event.stopPropagation();
      return false;
    }
    return true;
  }

  handlePaste(event: ClipboardEvent, index: number): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    
    if (pastedData.length === 0) return;
    
    // Si los datos pegados son un número, distribúyelos entre los campos
    if (/^\d+$/.test(pastedData)) {
      const digits = pastedData.slice(0, 5).split('');
      
      // Llena los campos de entrada con los dígitos pegados
      for (let i = 0; i < Math.min(5, digits.length); i++) {
        this.model[`code${i+1}`] = digits[i];
      }
      
      // Si hay menos de 5 dígitos, posiciona el cursor en el siguiente campo vacío
      if (digits.length < 5) {
        setTimeout(() => {
          const inputs = this.codeInputs.toArray();
          if (inputs[digits.length]) {
            inputs[digits.length].nativeElement.focus();
          }
        }, 0);
      } else {
        // Si todos los campos están llenos, verifica el código
        setTimeout(() => {
          if (this.model.validate()) {
            this.verifyCode();
          }
        }, 0);
      }
    }
  }

  resend(): void {

    this.loader.show();
    this.authService.notifyVerification(this.authUser!!.id)
      .subscribe({
        next: (res: any) => {
          this.showSendVerification = false;
          this.startTimer();
          this.loader.hide();
          this.success(`Código enviado a <strong> ${this.authUser!!.email} </strong>`);
        },
        error: err => {
          this.errorDefault();
          this.showSendVerification = true;
          this.loader.hide();
        },
        complete: () => {
          this.showSendVerification = false;
          this.loader.hide();
        }
      });
  }

  next(): void {
    const code = `${this.model.code1}${this.model.code2}${this.model.code3}${this.model.code4}${this.model.code5}`;
    this.loader.show();
    this.authService.verifyCode(code, this.authUser!!.id)
      .subscribe({
        next: async (res: any) => {
          if (!res.data.token) {
            this.errorDefault();
          }
          await this.authService.signInApply(res.data.token);
          await this.router.navigate(['/auth/complete-data']);

        },
        error: err => {
          if (err.status == 404)
            this.validationError = err.error.message;
          else
            this.errorDefault(err);
          this.loader.hide();
        },
        complete: () => {
          this.loader.hide();
        }
      });
  }

  navigateToRegister(): void {
    // Navegar a la página de registro y activar la pestaña de registro
    this.router.navigate(['/register'], { queryParams: { activeTab: 'register' } });
  }
}
